import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import HttpsIcon from '@mui/icons-material/Https';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PageHeader from '../components/common/PageHeader';
import { workbenchService, TestsslScanResult } from '../services/workbenchService';

const CHECKS_OPTIONS = [
  { value: 'standard', label: 'Standard (Übersicht)' },
  { value: 'protocols', label: 'Protokolle (-p)' },
  { value: 'ciphers', label: 'Cipher Suites (-E)' },
  { value: 'vulnerabilities', label: 'Schwachstellen (-U)' },
  { value: 'headers', label: 'HTTP Headers (-h)' },
  { value: 'full', label: 'Full Scan (--full)' },
];

const WorkbenchTestsslPage: React.FC = () => {
  const theme = useTheme();
  const [target, setTarget] = useState('');
  const [checks, setChecks] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestsslScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const canScan = target.trim().length > 0 && !loading;

  const handleScan = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await workbenchService.testsslScan(target.trim(), checks);
      setResult(data);
    } catch {
      setError('Scan fehlgeschlagen. Bitte Target prüfen und erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.output) return;
    try {
      await navigator.clipboard.writeText(result.output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* blocked */ }
  };

  return (
    <Box>
      <PageHeader
        title="SSL/TLS Scanner"
        subtitle="testssl.sh — Cipher Suites, Protokolle, Zertifikate und bekannte Schwachstellen prüfen"
        breadcrumbs={[
          { label: 'Workbench', path: '/workbench' },
          { label: 'SSL/TLS Scanner' },
        ]}
      />

      <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 700, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          label="Target"
          placeholder="z.B. example.com oder 192.168.1.1:443"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          fullWidth
          size="small"
        />

        <FormControl size="small" sx={{ maxWidth: 300 }}>
          <InputLabel>Check-Typ</InputLabel>
          <Select value={checks} label="Check-Typ" onChange={(e) => setChecks(e.target.value)}>
            {CHECKS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <HttpsIcon />}
          disabled={!canScan}
          onClick={handleScan}
          sx={{ alignSelf: 'flex-start', fontWeight: 700, px: 4, minWidth: 180 }}
        >
          {loading ? 'Scanne...' : 'Scan starten'}
        </Button>

        {error && (
          <Typography color="error" variant="body2">{error}</Typography>
        )}
      </Paper>

      {result && (
        <Paper sx={{ mt: 3, borderRadius: 2, maxWidth: 1000, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1,
              bgcolor: result.success
                ? `${theme.palette.success.main}18`
                : `${theme.palette.error.main}18`,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {result.success ? 'Scan erfolgreich' : 'Scan fehlgeschlagen'} — {result.target} ({result.checks})
            </Typography>
            <Tooltip title={copied ? 'Kopiert!' : 'Output kopieren'}>
              <IconButton size="small" onClick={handleCopy}>
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              p: 2,
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.78rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              maxHeight: 700,
              overflowY: 'auto',
              bgcolor: theme.palette.mode === 'dark' ? '#0d1117' : '#f6f8fa',
              color: theme.palette.mode === 'dark' ? '#c9d1d9' : '#24292f',
            }}
          >
            {result.output || 'Kein Output erhalten.'}
          </Box>
        </Paper>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="In Zwischenablage kopiert"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WorkbenchTestsslPage;
